// Shared with mju-ai-workbench/src/platform/subscriptions.ts — keep the two identical.
/**
 * People's own subscriptions (Claude Pro/Max, ChatGPT, GitHub Copilot, Google) on the machines that
 * run their work: their desktop, and the organisation's nodes, each person in their own profile.
 * mju-aioe docs/research/2026-10-subscription-auth-audit.md is the design.
 *
 * The rules, which no setting turns off:
 *   1. A subscription is used only by its own vendor's runtime (`SUBSCRIPTION_RUNTIME`): no proxy,
 *      no base-URL override, no translation between vendors.
 *   2. Credentials come from the vendor's own CLI, unmodified, on the machine that uses them, or
 *      as a token the vendor issues for headless use (`claude setup-token`, a GitHub token).
 *   3. One person's subscription serves only that person's runs, in their own profile.
 *   4. The organisation sees that a subscription exists and where, never the secret.
 *
 *   GET    /people/v1/me/subscriptions                  the person's subscriptions and pending sign-ins
 *   PUT    /people/v1/me/subscriptions/:vendor/token    keep a headless token (sealed)    { token }
 *   DELETE /people/v1/me/subscriptions/:vendor/token
 *   DELETE /people/v1/me/subscriptions/:vendor          sign out everywhere: drop the token, machines sign out
 *   DELETE /people/v1/me/subscriptions/sign-ins/:id     cancel a pending sign-in
 *   POST   /people/v1/me/subscriptions/sign-ins/:id/answer   what the vendor's page gave the person, for the node { answer }
 *   POST   /subscriptions/v1/report          a machine says what is signed in, per person (device)
 *   POST   /subscriptions/v1/release         a machine asks for a person's headless token for one run
 *   POST   /subscriptions/v1/sign-ins        a node asks a person to sign in there (the vendor's link or code)
 *   GET    /subscriptions/v1/sign-ins/:id            the node checks on it: cancelled, or the person's answer
 *   POST   /subscriptions/v1/sign-ins/:id/complete   the node says how it went
 *   GET    /admin/v1/subscriptions           the register: everyone's, no secrets
 *   DELETE /admin/v1/subscriptions/:principal/:vendor   revoke: the token is dropped, machines sign the profile out
 *   GET|PUT /admin/v1/subscriptions/settings
 *
 * A node names the person in `x-aioe-principal`, and may act for them only on a project it holds or
 * is assigned to and they are a member of, as for secrets.
 */
import { z } from "zod";
export const SUBSCRIPTION_VENDORS = ["anthropic", "openai", "github", "google"];
export const subscriptionVendorSchema = z.enum(SUBSCRIPTION_VENDORS);
/** The one runtime each vendor's subscription may run: the workbench's runtime ids. */
export const SUBSCRIPTION_RUNTIME = {
    anthropic: "claude-code",
    openai: "codex",
    github: "copilot",
    google: "gemini",
};
export const subscriptionRuntimeSchema = z.enum(["claude-code", "codex", "copilot", "gemini"]);
/** What people call each: for every screen that names one. */
export const SUBSCRIPTION_LABEL = {
    anthropic: { vendor: "Claude", runtime: "Claude Code" },
    openai: { vendor: "ChatGPT", runtime: "Codex" },
    github: { vendor: "GitHub Copilot", runtime: "Copilot CLI" },
    google: { vendor: "Google", runtime: "Gemini" },
};
/** The vendor whose subscription a runtime may use, if any. */
export function vendorOfRuntime(runtime) {
    return SUBSCRIPTION_VENDORS.find((v) => SUBSCRIPTION_RUNTIME[v] === runtime);
}
/** Rule 1: true only for a vendor's own runtime. */
export function subscriptionMayRun(vendor, runtime) {
    return SUBSCRIPTION_RUNTIME[vendor] === runtime;
}
/**
 * Tokens a vendor issues for headless use, and the one variable its runtime reads them from.
 * Anthropic: `claude setup-token` (a long-lived subscription token). GitHub: an OAuth or
 * fine-grained token with Copilot access. The others sign in on the machine only.
 */
export const HEADLESS_TOKEN = {
    anthropic: { env: "CLAUDE_CODE_OAUTH_TOKEN", pattern: /^sk-ant-oat01-[A-Za-z0-9_-]{20,}$/, howTo: "Run `claude setup-token` and paste the token it prints." },
    github: { env: "COPILOT_GITHUB_TOKEN", pattern: /^(gho_|ghu_|github_pat_)[A-Za-z0-9_]{20,}$/, howTo: "Use a GitHub token with Copilot access (an OAuth token, or a fine-grained token with the Copilot Requests permission)." },
};
export const headlessVendorSchema = z.enum(["anthropic", "github"]);
export const headlessTokenPutSchema = z.object({ token: z.string().min(20).max(2000) });
/** Where a profile's sign-in came from: the vendor's login on that machine, or a headless token released for the run. */
export const subscriptionStateSchema = z.enum(["signed-in", "signed-out", "expired", "refused"]);
/**
 * Cost round 2 (AIOE 0.18.0, optional): how much of a subscription's usage window is used, as the
 * vendor's runtime reports it: Claude's five-hour and weekly windows, a monthly allowance, Copilot's
 * premium requests. `used` is in `unit`; with `limit` (same unit) the platform works out the share,
 * and `unit: "percent"` needs none. The person hears at 80 and 100 per cent, once per window reset.
 */
export const SUBSCRIPTION_WINDOW_KINDS = ["5h", "week", "month", "premium-requests"];
export const subscriptionWindowSchema = z.object({
    kind: z.enum(SUBSCRIPTION_WINDOW_KINDS),
    used: z.number().min(0),
    limit: z.number().positive().optional(),
    /** `credits`: what Copilot bills its cloud agent in, a count. */
    unit: z.enum(["percent", "tokens", "requests", "usd", "credits"]),
    resetsAt: z.string().datetime().optional(),
});
/** The share of a window used, in per cent (rounded), or undefined when it cannot be told. */
export function subscriptionWindowPercent(w) {
    if (w.unit === "percent")
        return Math.round(w.used);
    return w.limit ? Math.round((w.used / w.limit) * 100) : undefined;
}
const WINDOW_WORDS = { "5h": "the five-hour window", week: "the weekly window", month: "the monthly allowance", "premium-requests": "the premium requests" };
/** Plain words for a window: "Claude: 62% of the five-hour window, resets in 1 h 20". */
export function subscriptionWindowWords(vendor, w, now = new Date()) {
    const pct = subscriptionWindowPercent(w);
    const amount = pct !== undefined ? `${pct}%` : `${w.used} ${w.unit === "usd" ? "US dollars" : w.unit}`;
    let reset = "";
    if (w.resetsAt) {
        const mins = Math.max(0, Math.round((Date.parse(w.resetsAt) - now.getTime()) / 60_000));
        const days = Math.floor(mins / 1440);
        const h = Math.floor((mins % 1440) / 60);
        const m = mins % 60;
        reset = `, resets in ${days ? `${days} d${h ? ` ${h} h` : ""}` : h ? `${h} h${m ? ` ${m}` : ""}` : `${m} min`}`;
    }
    return `${SUBSCRIPTION_LABEL[vendor].vendor}: ${amount} of ${WINDOW_WORDS[w.kind]}${reset}`;
}
/** One person's runtime on one machine, as the machine reports it. */
export const machineProfileSchema = z.object({
    principal: z.string().min(1).max(200),
    vendor: subscriptionVendorSchema,
    state: subscriptionStateSchema,
    via: z.enum(["machine", "token"]),
    /** As the credential says: "Max", "Pro", "Plus", "Business". */
    plan: z.string().max(40).optional(),
    /** The account's email or login, for the person and the register; never a secret. */
    account: z.string().max(200).optional(),
    expiresAt: z.string().datetime().optional(),
    /** How much of each usage window is used (cost round 2, optional). */
    windows: z.array(subscriptionWindowSchema).max(8).optional(),
});
/** `POST /subscriptions/v1/report`: everything signed in on this machine, every person's profile. Replaces the last report. */
export const subscriptionReportSchema = z.object({
    schemaVersion: z.literal(1),
    profiles: z.array(machineProfileSchema).max(500),
});
/** What the machine must do: sign these profiles out (revoked by the person or an administrator). */
export const subscriptionReportAnswerSchema = z.object({
    signOut: z.array(z.object({ principal: z.string(), vendor: subscriptionVendorSchema })),
});
/** `POST /subscriptions/v1/release`: a person's headless token for one run of their own runtime. */
export const subscriptionReleaseRequestSchema = z.object({
    runtime: subscriptionRuntimeSchema,
    /** The project the run is for; a node may act for a person only there. Optional for a person's own desktop. */
    projectId: z.string().min(1).max(200).optional(),
});
export const subscriptionReleaseSchema = z.object({
    vendor: headlessVendorSchema,
    runtime: subscriptionRuntimeSchema,
    /** Set exactly this variable in exactly this runtime's environment, for this run only. */
    env: z.object({ name: z.string(), value: z.string() }),
    leaseSeconds: z.number().int().positive(),
});
export const signInWantsSchema = z.enum(["code", "redirect-url"]);
/** `POST /subscriptions/v1/sign-ins`: the vendor's own login, started on the node, waits for the person. */
export const signInRequestSchema = z.object({
    vendor: subscriptionVendorSchema,
    projectId: z.string().min(1).max(200),
    /** The link the vendor's CLI printed. */
    url: z.string().url().max(2000).refine((u) => u.startsWith("https://"), "an https link"),
    /** The device code to enter there, when the vendor uses one. */
    code: z.string().max(40).optional(),
    /**
     * What the node needs back from the person, when the vendor's login waits for it: `code`, the code
     * the vendor's page shows after sign-in (Claude Code), or `redirect-url`, the address the browser
     * ended on, a loopback address on the node that the person's browser cannot open (Google).
     */
    wants: signInWantsSchema.optional(),
    expiresAt: z.string().datetime(),
});
export const signInStateSchema = z.enum(["pending", "done", "failed", "cancelled", "expired"]);
export const signInSchema = z.object({
    id: z.string(),
    principal: z.string(),
    vendor: subscriptionVendorSchema,
    projectId: z.string(),
    deviceId: z.string(),
    machine: z.string(),
    url: z.string(),
    code: z.string().optional(),
    wants: signInWantsSchema.optional(),
    /** The person has sent what the node wanted; the value itself is only in the node's view. */
    answered: z.boolean().optional(),
    state: signInStateSchema,
    message: z.string().optional(),
    createdAt: z.string(),
    expiresAt: z.string(),
    updatedAt: z.string(),
});
/** `GET /subscriptions/v1/sign-ins/:id`, for the node that asked: the record, and the person's answer while it is pending. */
export const signInNodeViewSchema = signInSchema.extend({ answer: z.string().optional() });
/** `POST /people/v1/me/subscriptions/sign-ins/:id/answer`. */
export const signInAnswerSchema = z.object({ answer: z.string().min(1).max(2000) });
/**
 * Whether an answer is the kind the node asked for. A redirect is a loopback http address (the node's
 * own listener) with a query; a code is one token without spaces. The node checks the rest (state).
 */
export function signInAnswerFits(wants, answer) {
    const a = answer.trim();
    if (wants === "code")
        return /^\S{4,500}$/.test(a);
    try {
        const u = new URL(a);
        return u.protocol === "http:" && (u.hostname === "127.0.0.1" || u.hostname === "localhost" || u.hostname === "[::1]") && u.search.length > 1;
    }
    catch {
        return false;
    }
}
export const signInCompleteSchema = z.object({
    ok: z.boolean(),
    plan: z.string().max(40).optional(),
    account: z.string().max(200).optional(),
    message: z.string().max(500).optional(),
});
/** The organisation's choices. Rule 1 is not among them: it cannot be switched off. */
export const subscriptionSettingsSchema = z.object({
    /** Whose subscriptions people may use on the organisation's machines. */
    allowedVendors: z.array(subscriptionVendorSchema),
    /** People may use their own subscription on a shared node, in their own profile. */
    sharedNodes: z.boolean(),
    /** People may keep a headless token here for their runs. */
    headlessTokens: z.boolean(),
});
export const DEFAULT_SUBSCRIPTION_SETTINGS = { allowedVendors: [...SUBSCRIPTION_VENDORS], sharedNodes: true, headlessTokens: true };
/** A subscription as the person and the register see it: never the secret. */
export const subscriptionSchema = z.object({
    principal: z.string(),
    vendor: subscriptionVendorSchema,
    runtime: subscriptionRuntimeSchema,
    plan: z.string().optional(),
    account: z.string().optional(),
    token: z.object({ addedAt: z.string(), lastReleasedAt: z.string().optional(), releases: z.number().int().nonnegative() }).optional(),
    machines: z.array(z.object({
        deviceId: z.string(),
        name: z.string(),
        kind: z.enum(["desktop", "node"]),
        state: subscriptionStateSchema,
        via: z.enum(["machine", "token"]),
        plan: z.string().optional(),
        account: z.string().optional(),
        expiresAt: z.string().optional(),
        seenAt: z.string(),
        /** How much of each usage window is used, as this machine last reported it (cost round 2, optional). */
        windows: z.array(subscriptionWindowSchema).optional(),
    })),
    revokedAt: z.string().optional(),
});
export const mySubscriptionsSchema = z.object({
    subscriptions: z.array(subscriptionSchema),
    signIns: z.array(signInSchema),
    settings: subscriptionSettingsSchema,
});
/**
 * The inbox action a sign-in request carries: clients show it as a link to `params.url` (with
 * `params.code` to copy), never as a call. The sign-in finishes on the node.
 */
export const SUBSCRIPTION_SIGN_IN_ACTION = "aioe.subscription.sign-in";
/** How long a released token may be held by the machine: the run's start, not its length. */
export const SUBSCRIPTION_LEASE_SECONDS = 900;
//# sourceMappingURL=subscriptions.js.map